import React from 'react';
import { ListButton, RefreshButton, Show, TextField } from '@refinedev/antd';
import { Typography, Card, Col, Row, Space } from 'antd';
import {
  InstagramOutlined,
  LinkedinOutlined,
  MinusOutlined,
  TwitterOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useTranslation } from '@refinedev/core';
import { useGet } from '@/api/useApi';
import { endPoints } from '@/api/configs';
import { GetOneUser } from '@/api/types/getOneUser';
import dayjs from 'dayjs';
import { Card as AntCard } from 'antd';

const { Title } = Typography;

export const ShowUser = () => {
  const { id } = useParams<{ id: string }>();
  const { translate } = useTranslation();
  const { data, isLoading } = useGet<GetOneUser>(
    endPoints.profile(id),
    endPoints.profile(id)
  );
  const userData = data?.data.data;

  return (
    <Show
      isLoading={isLoading}
      title={
        <div className="flex gap-x-3">
          <UserOutlined />
          <h1>{translate('show-user.title')}</h1>
        </div>
      }
      headerButtons={({ listButtonProps, refreshButtonProps }) => (
        <>
          {listButtonProps && (
            <ListButton {...listButtonProps}>
              {translate('buttons.list')}
            </ListButton>
          )}
          <RefreshButton {...refreshButtonProps}>
            {translate('buttons.refresh')}
          </RefreshButton>
        </>
      )}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={translate('show-user.sections.personalInfo')}
            bordered={false}
          >
            <div className="space-y-4">
              <div>
                <Title level={5}>{translate('show-user.fields.name')}</Title>
                <TextField
                  value={userData?.user_info?.name ?? <MinusOutlined />}
                />
              </div>

              <div>
                <Title level={5}>{translate('show-user.fields.quote')}</Title>
                <TextField
                  value={userData?.user_info?.quote ?? <MinusOutlined />}
                />
              </div>

              <div>
                <Title level={5}>
                  {translate('show-user.fields.description')}
                </Title>
                <TextField value={userData?.description ?? <MinusOutlined />} />
              </div>

              <div>
                <Title level={5}>
                  {translate('show-user.fields.createdAt')}
                </Title>
                <TextField
                  value={
                    userData?.created_at ? (
                      dayjs(userData.created_at).format('DD-MM-YYYY')
                    ) : (
                      <MinusOutlined />
                    )
                  }
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={translate('show-user.sections.classInfo')}
            bordered={false}
          >
            <div className="space-y-4">
              <div>
                <Title level={5}>
                  {translate('show-user.fields.className')}
                </Title>
                <TextField
                  value={userData?.class_info?.name ?? <MinusOutlined />}
                />
              </div>

              <div>
                <Title level={5}>
                  {translate('show-user.fields.dateRange')}
                </Title>
                <TextField
                  value={userData?.class_info?.date_range ?? <MinusOutlined />}
                />
              </div>

              <div>
                <Title level={5}>
                  {translate('show-user.fields.classDate')}
                </Title>
                <TextField
                  value={
                    userData?.class_info?.date ? (
                      dayjs(userData.class_info.date).format('DD-MM-YYYY')
                    ) : (
                      <MinusOutlined />
                    )
                  }
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={
              <div className="flex gap-x-3">
                <h1>{translate('show-user.sections.socialMedia')}</h1>
              </div>
            }
            bordered={false}
          >
            <div className="flex gap-x-8">
              <div>
                <Title level={5}>
                  <Space>
                    {translate('show-user.fields.linkedin')}
                    <LinkedinOutlined />
                  </Space>
                </Title>
                {userData?.social_media?.linkedin_url ? (
                  <a
                    href={userData?.social_media?.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {userData?.social_media?.linkedin_url}
                  </a>
                ) : (
                  <MinusOutlined />
                )}
              </div>

              <div>
                <Title level={5}>
                  <Space>
                    {translate('show-user.fields.instagram')}
                    <InstagramOutlined />
                  </Space>
                </Title>
                {userData?.social_media?.instagram_url ? (
                  <a
                    href={userData?.social_media?.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {userData?.social_media?.instagram_url}
                  </a>
                ) : (
                  <MinusOutlined />
                )}
              </div>

              <div>
                <Title level={5}>
                  <Space>
                    {translate('show-user.fields.twitter')}
                    <TwitterOutlined />
                  </Space>
                </Title>
                {userData?.social_media?.x_url ? (
                  <a
                    href={userData?.social_media?.x_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {userData?.social_media?.x_url}
                  </a>
                ) : (
                  <MinusOutlined />
                )}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title={translate('show-user.sections.posts')} bordered={false}>
            <div className="space-y-4">
              <div>
                <Title level={5}>
                  {translate('show-user.fields.postsCount')}
                </Title>
                <TextField
                  value={userData?.public_posts?.count ?? <MinusOutlined />}
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={
              <div className="flex gap-x-3">
                <h1>{translate('show-user.sections.achievements')}</h1>
                <UnorderedListOutlined />
              </div>
            }
            bordered={false}
          >
            <div className="space-y-4">
              {userData?.achievements_by_type?.ACHIEVEMENT &&
              userData.achievements_by_type.ACHIEVEMENT.length > 0 ? (
                userData.achievements_by_type.ACHIEVEMENT.map((achievement) => (
                  <AntCard key={achievement.id}>
                    <Title level={5}>{achievement.name}</Title>
                    <div className="flex flex-col">
                      <div className="flex gap-x-2">
                        <TextField
                          value={translate('show-user.fields.organization')}
                        />
                        <TextField
                          value={
                            achievement.granting_organization ?? (
                              <MinusOutlined />
                            )
                          }
                        />
                      </div>
                      <div className="flex gap-x-2">
                        <TextField value={translate('show-user.fields.date')} />
                        <TextField
                          value={`${dayjs(
                            achievement.date_of_acquisition_start
                          ).format(
                            'DD-MM-YYYY'
                          )} - ${dayjs(achievement.date_of_acquisition_end).format('DD-MM-YYYY')}`}
                        />
                      </div>
                      <div className="flex gap-x-2">
                        <TextField value={translate('show-user.fields.url')} />
                        <TextField
                          value={
                            achievement.photo_url ? (
                              <a
                                href={achievement.photo_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {achievement.photo_url}
                              </a>
                            ) : (
                              <MinusOutlined />
                            )
                          }
                        />
                      </div>
                      <div className="flex gap-x-2">
                        <TextField
                          value={translate(
                            'show-user.fields.related_media_url'
                          )}
                        />
                        <TextField
                          value={
                            achievement.related_media_url ? (
                              <a
                                href={achievement.related_media_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {achievement.related_media_url}
                              </a>
                            ) : (
                              <MinusOutlined />
                            )
                          }
                        />
                      </div>
                    </div>
                  </AntCard>
                ))
              ) : (
                <MinusOutlined />
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </Show>
  );
};
